import { useMutation, useQuery } from '@tanstack/react-query';

import { API, QUERY_KEYS } from '@config';
import { useRequest } from '@hooks/useRequest';

export type RunLogItem = {
  _id: string;
  start: string;
  status: { name: string };
  trigger_type?: string;
};

export type NodeInputPreview = {
  input_data: Record<string, unknown>;
  unserializable_keys: string[];
  missing_references: string[];
};

/** Recent runs of a flow, newest first — the run-from-here source picker. */
export const useRecentFlowRunsQuery = (
  flowUuid: string | undefined,
  { enabled }: { enabled: boolean },
) => {
  const request = useRequest();

  return useQuery({
    queryKey: [QUERY_KEYS.FLOW_RECENT_RUNS, flowUuid],
    queryFn: async (): Promise<RunLogItem[]> => {
      const params = new URLSearchParams({
        flow_uuids: flowUuid as string,
        limit: '20',
        sort_by: 'start',
        sort_order: 'desc',
      }).toString();
      const { data } = await request<{ logs: RunLogItem[] }>(
        API.ANALYTICS_FLOWS_LOGS(params),
      );
      return data.logs ?? [];
    },
    enabled: enabled && !!flowUuid,
  });
};

/**
 * Preview what a node consumed in a prior run, keyed exactly as the
 * execute-from-node override accepts. User-initiated (Load data button),
 * hence a mutation.
 */
export const usePreviewNodeInputMutation = (flowUuid: string | undefined) => {
  const request = useRequest();

  return useMutation({
    mutationFn: async ({
      nodeUuid,
      sourceRunId,
    }: {
      nodeUuid: string;
      sourceRunId: string;
    }): Promise<NodeInputPreview> => {
      const { data } = await request<NodeInputPreview>(
        API.FLOWS_EXECUTE_FROM_NODE_PREVIEW(flowUuid as string),
        { params: { node_uuid: nodeUuid, source_mongo_flow_id: sourceRunId } },
      );
      return data;
    },
  });
};
